import { AuditStamp } from '../../../components/ui/AuditStamp';
import { soldierDisplayName } from '../../../core/soldier-roles';
import { AVAILABILITY_ICONS, TIMED_STATUS_ICONS } from '../../../core/soldier-status';
import {
  EMPTY_ASSIGNMENT_INDEX,
  describeEffectiveSoldierStatus,
  effectiveSoldierAvailability,
  effectiveSoldierStatusIcon,
} from '../../../core/effective-status';
import type { AssignmentIndex } from '../../../core/effective-status';
import type { Soldier, SoldierAvailability } from '../../../types/fleet';

/** Colour per availability, shared with the roster tabs. */
export const AVAILABILITY_STYLES: Record<SoldierAvailability, { dot: string; chip: string }> = {
  available: { dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  on_mission: { dot: 'bg-brand-500', chip: 'bg-brand-50 text-brand-700 border-brand-100' },
  leave: { dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  unavailable: { dot: 'bg-rose-500', chip: 'bg-rose-50 text-rose-700 border-rose-200' },
};

/**
 * The status glyphs live in `src/core/soldier-status.ts`, beside the sentence
 * they lead — the quick-status sheet paints them too. Re-exported here because
 * this is where the roster has always read them from.
 */
export { AVAILABILITY_ICONS, TIMED_STATUS_ICONS };

interface SoldierCardProps {
  soldier: Soldier;
  /** Who is out on an active task — see `src/core/effective-status.ts`. */
  assignments?: AssignmentIndex;
  /** Evaluation moment for the timed status; injected so it can be frozen. */
  now?: number;
  onOpen: (soldier: Soldier) => void;
  /**
   * Tapping the status badge opens the one-tap status sheet. Omitted without
   * `MANAGE_SOLDIERS`, and the badge is then a plain label again.
   */
  onQuickStatus?: (soldier: Soldier) => void;
}

/** The two-letter monogram the roster leads a soldier's row with. */
function initialsOf(fullName: string): string {
  const words = fullName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '—';
  return words.slice(0, 2).map((word) => word[0]).join('');
}

/**
 * One soldier in the roster, kept deliberately short: a monogram, the name and
 * the job it belongs to, the status, and the one way in.
 *
 * The name line reads "בן פרץ - מ"פ" — {@link soldierDisplayName} in
 * `src/core/soldier-roles.ts` is what spells it, so the roster row and anything
 * else that names a soldier say it the same way. The job is on the row because
 * the roster is read at a glance and nothing else on it says who commands the
 * company; it is not a second field, it is part of the name line.
 *
 * Everything else the company holds on them — נייד, מ.א, דרגת רישיון, the
 * vehicles that opens and the edit and delete controls — lives behind
 * "כרטיס חייל" in {@link SoldierDetailsModal}. That is what lets a screen hold
 * the whole platoon at once instead of four soldiers and a scrollbar.
 *
 * A temporary status is still shown as the sentence the manager set it with —
 * "בבית עד יום שלישי 08:00" — and takes precedence over the stored availability
 * until it lapses. The badge carrying that sentence is itself the control: one
 * tap opens the status sheet, so saying that a soldier went home never means
 * opening the edit form.
 *
 * A soldier riding an active task reads "🚚 במשימה" without anybody having set
 * it: that comes off the convoy itself, through
 * `src/core/effective-status.ts`, and lapses by itself when the task is closed.
 */
export function SoldierCard({
  soldier,
  assignments = EMPTY_ASSIGNMENT_INDEX,
  now = Date.now(),
  onOpen,
  onQuickStatus,
}: SoldierCardProps) {
  const state = effectiveSoldierAvailability(soldier, assignments, now);
  const displayName = soldierDisplayName(soldier);
  const style = AVAILABILITY_STYLES[state];
  const statusText = describeEffectiveSoldierStatus(soldier, assignments, now);
  const icon = effectiveSoldierStatusIcon(soldier, assignments, now);
  const badgeClass = `mt-0.5 inline-flex max-w-full items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-lg border ${style.chip}`;

  return (
    <article className="w-full bg-panel border border-line rounded-2xl px-3 py-2.5 shadow-sm flex items-center gap-3">
      <div
        className="shrink-0 w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black bg-brand-50 text-brand-700"
        aria-hidden
      >
        {initialsOf(soldier.fullName)}
      </div>

      <div className="min-w-0 grow">
        <h3 className="font-bold text-sm truncate" title={displayName}>
          {displayName}
        </h3>
        {onQuickStatus ? (
          <button
            type="button"
            data-quick-status
            onClick={() => onQuickStatus(soldier)}
            title="שינוי סטטוס מהיר"
            aria-label={`שינוי סטטוס — ${displayName}, ${statusText}`}
            className={`${badgeClass} hover:brightness-95 active:brightness-90 cursor-pointer`}
          >
            <span aria-hidden>{icon}</span>
            <span className="truncate">{statusText}</span>
            <span aria-hidden className="text-[9px] opacity-60">
              ▾
            </span>
          </button>
        ) : (
          <span className={badgeClass}>
            <span aria-hidden>{icon}</span>
            <span className="truncate">{statusText}</span>
          </span>
        )}

        {/* "מי עידכן מה" — whose word this soldier's status is. A status set
            from a phone in the field is only worth reading with a name on it. */}
        <AuditStamp record={soldier} now={now} className="mt-0.5" />
      </div>

      <button
        type="button"
        onClick={() => onOpen(soldier)}
        className="shrink-0 h-8 px-3 rounded-xl border border-brand-500 bg-brand-500 text-white text-xs font-medium hover:bg-brand-700"
      >
        כרטיס חייל
      </button>
    </article>
  );
}
